import { Stack } from "@mui/material";
import { SharedBusinessFields } from "Components/Onboarding/BusinessEntity/Steps/IncomeInformationStep/SharedBusinessFields";
import type { IncomeInformationStepFormData } from "Components/Onboarding/BusinessEntity/Steps/IncomeInformationStep/schema";
import { PoliticallyExposedCheckbox } from "Components/Onboarding/shared/PoliticallyExposedCheckbox";
import { FormSection } from "Components/Shared/FormSection";
import { CheckboxInput } from "Components/Shared/Inputs/Form/CheckboxInput";
import { AverageTransactionValueDropdown } from "Components/Shared/SelectorsForm/AverageTransactionValueDropdown";
import { CountryDropdown } from "Components/Shared/Inputs/Form/Dropdowns/CountryDropdown";
import { ExpectedMonthlyRevenueDropdown } from "Components/Shared/SelectorsForm/ExpectedMonthlyRevenueDropdown";
import { GroupedRelationTypeSelector } from "Components/Shared/SelectorsForm/GroupedRelationTypeSelector";
import {
  type BusinessActivity,
  PartyType,
  type SourceOfIncome,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import type { Control } from "react-hook-form";

const COMPONENT_RESOURCES =
  Resources.Onboarding.BusinessOnboarding.Steps.IncomeInformation;

type Props = {
  control: Control<IncomeInformationStepFormData>;
  partyType?: PartyType;
  sourcesOfIncome: Array<SourceOfIncome>;
  businessActivity?: BusinessActivity;
  isPoliticallyExposedPerson: boolean;
};

export const MerchantIncomeInformationFields = ({
  control,
  partyType,
  sourcesOfIncome,
  businessActivity,
  isPoliticallyExposedPerson,
}: Props) => {
  const { t } = useResource();

  return (
    <>
      <FormSection legend={t(COMPONENT_RESOURCES.FormGroups.Business)}>
        <SharedBusinessFields
          control={control}
          businessActivity={businessActivity}
          sourcesOfIncome={sourcesOfIncome}
        />
        <ExpectedMonthlyRevenueDropdown
          control={control}
          name="expectedMonthlyRevenue"
          label={t(COMPONENT_RESOURCES.Fields.ExpectedMonthlyRevenue)}
        />
        <AverageTransactionValueDropdown
          control={control}
          name="averageTransactionValue"
          label={t(COMPONENT_RESOURCES.Fields.AverageTransactionValue)}
        />
        <CountryDropdown
          control={control}
          name="taxResidency"
          label={t(COMPONENT_RESOURCES.Fields.TaxResidencyCountry)}
        />
      </FormSection>
      <FormSection legend={t(COMPONENT_RESOURCES.FormGroups.Personal)}>
        {partyType === PartyType.LegalEntity && (
          <GroupedRelationTypeSelector
            control={control}
            name="relationType"
            label={t(COMPONENT_RESOURCES.Fields.RelationType)}
          />
        )}
        <Stack gap={2}>
          <PoliticallyExposedCheckbox
            control={control}
            name="isPoliticallyExposedPerson"
            isChecked={isPoliticallyExposedPerson}
          />
          <CheckboxInput
            label={t(COMPONENT_RESOURCES.Fields.HasNonEUResidence)}
            control={control}
            name="hasNonEUResidence"
          />
          <CheckboxInput
            label={t(COMPONENT_RESOURCES.Fields.HasNonEUNationality)}
            control={control}
            name="hasNonEUNationality"
          />
        </Stack>
      </FormSection>
    </>
  );
};
